import { useEffect, useState } from "react";
import { grossesBild } from "@/lib/bildpfade";

/**
 * Zeigt das Bild eines Extras in voller Größe.
 *
 * In der Paketliste stand das Bild vorher als kleines Vorschaubild anstelle
 * des Häkchens. Das kostete mehr, als es brachte: Die Zeilen begannen dann
 * mal mit einem Häkchen, mal mit einem Bild, und die Liste ließ sich nicht
 * mehr überfliegen. Jetzt beginnt jede Zeile gleich, und wer das Bild sehen
 * will, holt es sich.
 *
 * Geladen wird hier die große Fassung — die kleine ist auf Listengröße
 * gerechnet und sähe auf 70 Prozent Bildschirmhöhe weich aus. Umgekehrt wäre
 * es Verschwendung, in einer Liste die große auszuliefern; deshalb zwei
 * Auflösungen unter verwandten Adressen.
 *
 * Bilder von vor dem 2026-08-23 haben keine große Fassung. Für sie zeigt der
 * Rückfall die kleine: etwas weich ist besser als ein leerer Rahmen.
 */
export default function BildModal({ bild, titel, onClose }) {
  const [quelle, setQuelle] = useState(() => grossesBild(bild));

  // Escape schließt — bei einem Overlay erwartet man das, und ohne bleibt
  // nur der Weg über die Maus.
  useEffect(() => {
    const zu = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", zu);
    // Solange das Bild offen ist, soll die Seite dahinter nicht scrollen.
    const vorher = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", zu);
      document.body.style.overflow = vorher;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={titel}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-full w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow ring-1 ring-slate-200 hover:text-slate-900"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" className="h-4 w-4">
            <path
              d="M4 4l8 8M12 4l-8 8"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* eslint-disable-next-line @next/next/no-img-element */}
        {/* Kein w-full: Ein kleines Bild wird nicht hochgezogen, sondern in
            seiner echten Größe gezeigt. Alle aus Bubble übernommenen
            Extra-Bilder sind 180×250 — auf Modalbreite gestreckt sahen sie
            matschig aus. Lieber klein und scharf als groß und verwaschen;
            wer mehr will, lädt ein größeres Bild hoch. */}
        <div className="flex max-h-[70vh] items-center justify-center bg-slate-50 p-3">
          <img
            src={quelle}
            alt={titel}
            // Gibt es keine große Fassung, greift der Rückfall. Ohne ihn
            // stünde bei jedem der aus Bubble übernommenen Bilder ein leerer
            // Rahmen.
            onError={() => quelle !== bild && setQuelle(bild)}
            className="max-h-[calc(70vh-1.5rem)] max-w-full object-contain"
          />
        </div>
        <p className="border-t border-slate-100 px-4 py-3 text-sm font-medium text-slate-900">
          {titel}
        </p>
      </div>
    </div>
  );
}

/**
 * Der Textlink, der das Bild öffnet.
 *
 * Bewusst ein span mit role="button" und kein button: In der Paketliste
 * steckt er innerhalb der Paket-Schaltfläche, und ein Knopf im Knopf ist
 * ungültiges HTML. stopPropagation verhindert, dass der Klick nebenbei das
 * Paket auswählt.
 */
export function BildLink({ onClick }) {
  const oeffnen = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onClick();
  };
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={oeffnen}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && oeffnen(e)}
      className="cursor-pointer text-xs text-slate-500 underline decoration-slate-300 underline-offset-2 hover:text-slate-800"
    >
      Bild ansehen
    </span>
  );
}
